// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Alarm-rule registry client (alarm-rule-api, relayed by the BFF). Used to
// resolve an alarm's SOP: fetch the rule, then its `related_documents` SOP entry
// → document_id → SOP Factory artifact, and to fold the rule's fields (title,
// priority, category, …) into the Alarm. There is no FK from the alarm event to
// this registry, so a 404 is a normal outcome. See useAlarms.loadAlarmWithSop.

import { get, ApiError } from './api';
import { ENDPOINTS } from '../utils/constants';
import type { AlarmRule } from '../types/alarmApi';

/**
 * Fetch one alarm rule; null when the registry has no rule for this id (404).
 *
 * Optional resource — `resolveAlarmRule` below deliberately skips a candidate
 * that errors, so a 401 here must not tear down the session on the way past.
 */
export const getAlarmRule = async (siteId: string, ruleId: string): Promise<AlarmRule | null> => {
  try {
    return await get<AlarmRule>(ENDPOINTS.ALARM_RULE_BY_ID(siteId, ruleId), {
      endSessionOn401: false,
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
};

/**
 * First registered rule among the candidate ids, tried in order. The alarm's
 * rule_id matches its `alarm_code` by convention (there is no FK); a candidate
 * that 404s is skipped, and one that errors is skipped in favour of the next.
 *
 * Resolves null only when every candidate was a genuine miss. If none resolved
 * and at least one errored, the error is rethrown: "the registry is down" must
 * not read as "this alarm has no rule", or the caller invents a severity.
 */
export const resolveAlarmRule = async (
  siteId: string,
  candidateRuleIds: string[]
): Promise<AlarmRule | null> => {
  let lastError: unknown;
  for (const ruleId of candidateRuleIds) {
    try {
      const rule = await getAlarmRule(siteId, ruleId);
      if (rule) return rule;
    } catch (err) {
      lastError = err;
    }
  }
  if (lastError !== undefined) throw lastError;
  return null;
};

/** SOP document id from a rule's related_documents, if any. */
export const sopIdFromRule = (rule: AlarmRule | null): string | undefined =>
  rule?.related_documents?.find(d => d.document_type === 'SOP')?.document_id;
